import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect } from 'react';
import moment from 'moment';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { isEmbeddedDocument } from '@trello/browser';
import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import type { PIIString } from '@trello/privacy';
import { JoinBoardModalTestIds } from '@trello/test-ids';

import { PopUpWindow } from 'app/scripts/views/lib/PopUpWindow';
import { useCurrentBoardBackground } from 'app/src/components/BoardTheme';
import { TrelloMarkdown } from 'app/src/components/TrelloMarkdown';

import styles from './JoinBoardModal.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import popperSvg from 'resources/images/popper.svg';

const format = forTemplate('join_board_modal', {
  shouldEscapeStrings: false,
});

export interface JoinBoardModalProps {
  idBoard: string;
  idOrganization: string;
  inviterName: PIIString | undefined;
  boardName: string;
  signupUrl: string;
  desc?: string;
  descData: {
    emoji: {
      [key: string]: string;
    };
  };
  dateLastActivity: string | null;
  boardOpenCardCount: number;
}

export const JoinBoardModal: FunctionComponent<JoinBoardModalProps> = ({
  idBoard,
  idOrganization,
  inviterName,
  boardName,
  signupUrl,
  desc,
  descData,
  dateLastActivity,
  boardOpenCardCount,
}) => {
  const { backgroundTopColor } = useCurrentBoardBackground();

  const onClickJoinBoard = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'joinBoardButtonV2',
      source: 'joinBoardModal',
      containers: formatContainers({
        idBoard,
        idOrganization,
        idWorkspace: idOrganization,
      }),
    });

    if (isEmbeddedDocument()) {
      return new PopUpWindow(signupUrl).open();
    } else {
      window.location.href = signupUrl;
      return;
    }
  }, [idBoard, idOrganization, signupUrl]);

  const formatDateLastActive = () => {
    if (!dateLastActivity) return null;

    const diff = moment().diff(dateLastActivity, 'days');
    if (diff === 0) {
      return format('last-active-today');
    }
    if (diff === 1) {
      return format('last-active-yesterday');
    }
    return format('last-active-on', {
      date: moment(dateLastActivity).format('LL'),
    });
  };

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'joinBoardModal',
      containers: formatContainers({
        idBoard,
        idOrganization,
        idWorkspace: idOrganization,
      }),
      attributes: {
        boardDateLastActive: dateLastActivity,
        boardOpenCardCount,
      },
    });
  }, [idBoard, idOrganization, dateLastActivity, boardOpenCardCount]);

  return (
    <div
      className={styles.overlay}
      style={{
        background: backgroundTopColor
          ? `linear-gradient(#00000000 17.74%, ${backgroundTopColor} 81.87%)`
          : '',
      }}
      data-testid={JoinBoardModalTestIds.Overlay}
    >
      <div className={styles.joinBoardModal}>
        <div className={styles.popperImage}>
          <img src={popperSvg} alt="" role="presentation" />
        </div>
        <h2 className={styles.header}>
          {format(
            inviterName
              ? 'join-the-board-to-start-collaborating-with-inviter'
              : 'youve-been-invited-to-collaborate-on-board',
            {
              inviterName,
              boardName: (
                <span key="board-name" className={styles.boardName}>
                  {boardName}
                </span>
              ),
            },
          )}
        </h2>
        {dateLastActivity && (
          <p
            className={styles.date}
            data-testid={JoinBoardModalTestIds.DateLastActive}
          >
            {formatDateLastActive()}
          </p>
        )}
        {desc && (
          <div className={styles.description}>
            <p className={styles.descriptionHeader}>
              {format('about-this-board')}
            </p>
            <div className={styles.descriptionContent}>
              <TrelloMarkdown
                text={desc}
                options={{ textData: descData }}
                contentType="description"
              />
            </div>
          </div>
        )}
        <Button
          className={styles.button}
          appearance="primary"
          onClick={onClickJoinBoard}
          aria-label={format('join-board')}
          testId={JoinBoardModalTestIds.CTA}
        >
          {format('join-board')}
        </Button>
      </div>
    </div>
  );
};
